/**
 * Custom hook for managing conversations
 * Handles CRUD operations, selection, and conversation-related state
 */

import { useCallback, useEffect, useMemo, useState } from 'react';
import { generateConversationTitle } from '../lib/chat-utils';
import { chatService } from '../services/chatService';
import type { ChatError, Conversation } from '../types/chat';

export function useConversations() {
  const [conversations, setConversations] = useState<Conversation[]>([]);
  const [currentConversationId, setCurrentConversationId] = useState<string>('');
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<ChatError | null>(null);

  /**
   * Load all conversations from the database
   */
  const loadConversations = useCallback(async () => {
    try {
      setIsLoading(true);
      setError(null);

      const loadedConversations = await chatService.getAllConversations();
      console.log('🔍 DEBUG: Loaded conversations from database:', loadedConversations);

      setConversations(loadedConversations);

      // Auto-select the most recent conversation if none is selected
      // Using functional update to avoid dependency on currentConversationId
      if (loadedConversations.length > 0) {
        setCurrentConversationId((prev) => {
          if (prev) return prev; // Already set, don't change
          console.log('🔍 DEBUG: Set current conversation ID:', loadedConversations[0].id);
          return loadedConversations[0].id;
        });
      }
    } catch (err) {
      const chatError = chatService.handleError(err);
      setError(chatError);
      console.error('Failed to load conversations:', chatError);
    } finally {
      setIsLoading(false);
    }
  }, []);

  /**
   * Create a new conversation
   */
  const createNewConversation = useCallback(
    async (selectedModel?: string, selectedCharacter?: string | null, customPrompt?: string) => {
      try {
        setError(null);

        const newConversation = await chatService.createConversation({
          title: 'New Chat',
          createdAt: new Date(),
          updatedAt: new Date(),
          selectedModels: selectedModel ? [selectedModel] : [],
          lastSelectedModel: selectedModel || '',
          messageCount: 0,
          systemPromptType: selectedCharacter || undefined,
          systemPromptText: selectedCharacter === 'custom' ? customPrompt : undefined,
        });

        const updatedConversations = [newConversation, ...conversations];
        setConversations(updatedConversations);
        setCurrentConversationId(newConversation.id);

        console.log('✨ Created new conversation:', newConversation.id);
      } catch (err) {
        const chatError = chatService.handleError(err);
        setError(chatError);
        console.error('Failed to create new conversation:', chatError);
      }
    },
    [conversations]
  );

  /**
   * Select a conversation
   */
  const selectConversation = useCallback(async (conversationId: string) => {
    try {
      setError(null);
      setCurrentConversationId(conversationId);
      console.log('📍 Selected conversation:', conversationId);
    } catch (err) {
      const chatError = chatService.handleError(err);
      setError(chatError);
      console.error('Failed to select conversation:', chatError);
    }
  }, []);

  /**
   * Delete a conversation
   */
  const deleteConversation = useCallback(
    async (conversationId: string) => {
      try {
        setError(null);

        await chatService.deleteConversation(conversationId);

        const updatedConversations = conversations.filter((conv) => conv.id !== conversationId);
        setConversations(updatedConversations);

        // Handle current conversation deletion
        if (currentConversationId === conversationId) {
          if (updatedConversations.length > 0) {
            const nextConversation = updatedConversations[0];
            setCurrentConversationId(nextConversation.id);
          } else {
            // Create a new conversation if no conversations remain
            await createNewConversation();
          }
        }

        console.log('🗑️ Deleted conversation:', conversationId);
      } catch (err) {
        const chatError = chatService.handleError(err);
        setError(chatError);
        console.error('Failed to delete conversation:', chatError);
      }
    },
    [conversations, currentConversationId, createNewConversation]
  );

  /**
   * Update conversation title (typically from first message)
   */
  const updateConversationTitle = useCallback(async (conversationId: string, title: string) => {
    try {
      setError(null);

      const generatedTitle = generateConversationTitle(title);
      await chatService.updateConversationTitle(conversationId, generatedTitle);

      // Update local state
      setConversations((prev) =>
        prev.map((conv) =>
          conv.id === conversationId
            ? { ...conv, title: generatedTitle, updatedAt: new Date() }
            : conv
        )
      );

      console.log('📝 Updated conversation title:', conversationId, generatedTitle);
    } catch (err) {
      const chatError = chatService.handleError(err);
      setError(chatError);
      console.error('Failed to update conversation title:', chatError);
    }
  }, []);

  /**
   * Update conversation message count and timestamp
   */
  const updateConversationMetadata = useCallback(
    (conversationId: string, increment: number = 1) => {
      setConversations((prev) =>
        prev.map((conv) =>
          conv.id === conversationId
            ? {
                ...conv,
                messageCount: Math.max(0, conv.messageCount + increment),
                updatedAt: new Date(),
              }
            : conv
        )
      );
    },
    []
  );

  /**
   * Update conversation's last selected model
   */
  const updateConversationModel = useCallback(
    async (conversationId: string, model: string) => {
      try {
        setError(null);

        const conversation = conversations.find((conv) => conv.id === conversationId);
        if (!conversation) return;

        const updatedConversation = {
          ...conversation,
          lastSelectedModel: model,
          selectedModels: conversation.selectedModels.includes(model)
            ? conversation.selectedModels
            : [...conversation.selectedModels, model],
          updatedAt: new Date(),
        };

        await chatService.updateConversation(conversationId, updatedConversation);

        // Update local state
        setConversations((prev) =>
          prev.map((conv) => (conv.id === conversationId ? updatedConversation : conv))
        );
      } catch (err) {
        const chatError = chatService.handleError(err);
        setError(chatError);
        console.error('Failed to update conversation model:', chatError);
      }
    },
    [conversations]
  );

  /**
   * Update conversation with arbitrary fields
   */
  const updateConversation = useCallback(
    async (conversationId: string, updates: Partial<Conversation>) => {
      try {
        setError(null);

        await chatService.updateConversation(conversationId, updates);

        // Update local state
        setConversations((prev) =>
          prev.map((conv) =>
            conv.id === conversationId ? { ...conv, ...updates, updatedAt: new Date() } : conv
          )
        );

        console.log('📝 Updated conversation:', conversationId, updates);
      } catch (err) {
        const chatError = chatService.handleError(err);
        setError(chatError);
        console.error('Failed to update conversation:', chatError);
      }
    },
    []
  );

  /**
   * Get current conversation
   */
  const getCurrentConversation = useCallback(() => {
    return conversations.find((conv) => conv.id === currentConversationId) || null;
  }, [conversations, currentConversationId]);

  // Load conversations on mount
  useEffect(() => {
    loadConversations();
  }, [loadConversations]);

  // Actions object for external use (memoized to prevent re-renders)
  const actions = useMemo(
    () => ({
      createNew: createNewConversation,
      select: selectConversation,
      delete: deleteConversation,
      updateTitle: updateConversationTitle,
      updateModel: updateConversationModel,
      updateConversation: updateConversation,
      updateMetadata: updateConversationMetadata,
      reload: loadConversations,
    }),
    [
      createNewConversation,
      selectConversation,
      deleteConversation,
      updateConversationTitle,
      updateConversationModel,
      updateConversation,
      updateConversationMetadata,
      loadConversations,
    ]
  );

  // Memoize return value to prevent unnecessary re-renders in consumers
  const result = useMemo(
    () => ({
      conversations,
      currentConversationId,
      isLoading,
      error,
      actions,
      getCurrentConversation,
    }),
    [conversations, currentConversationId, isLoading, error, actions, getCurrentConversation]
  );

  return result;
}
